import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Toaster, toast } from "sonner";
import {
  Zap, Users, MapPin, Calendar, PenLine, Send, ArrowLeft,
  BookOpen, ChevronDown, Share2, Link2, Check, Copy
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOAST_OPTIONS = {
  style: {
    background: "#121212",
    border: "1px solid rgba(255,255,255,0.15)",
    color: "#fff",
    borderRadius: "0",
  },
};

const NAV_LINKS = [
  { label: "HOME", href: "/" },
  { label: "COMMUNITY", href: "/community" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []); // handleScroll defined inside effect; setScrolled is stable

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-black/80 border-b border-white/10" : "bg-[#0A0A0A]"
      }`}
      data-testid="community-header"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group">
          <Zap className="w-6 h-6 text-[#FF3B30] group-hover:scale-110 transition-transform" />
          <span className="font-['Anton'] text-xl uppercase tracking-wider text-white">
            EMF<span className="text-[#FF3B30]">AWARE</span>
          </span>
        </Link>
        <nav className="flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="text-xs uppercase tracking-[0.2em] font-bold text-white/70 hover:text-[#FF3B30] transition-colors duration-300"
            >
              {link.label}
            </Link>
          ))}
        </nav>
      </div>
    </header>
  );
};

const SITE_URL = window.location.origin;

const ShareButton = ({ href, label, icon, className, onClick, testId }) => {
  if (onClick) {
    return (
      <button
        onClick={onClick}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider font-bold border transition-all duration-300 ${className}`}
        data-testid={testId}
        title={label}
      >
        {icon}
        <span className="hidden sm:inline">{label}</span>
      </button>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider font-bold border transition-all duration-300 ${className}`}
      data-testid={testId}
      title={label}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </a>
  );
};

const XIcon = () => (
  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" aria-hidden="true">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const RedditIcon = () => (
  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" aria-hidden="true">
    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
  </svg>
);

const StoryCard = ({ story }) => {
  const [copied, setCopied] = useState(false);
  const shareText = `"${story.title}" by ${story.author_name} — an EMF exposure experience shared on EMF Aware`;
  const shareUrl = `${SITE_URL}/community`;

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const redditUrl = `https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(story.title + " — EMF Aware Community")}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className="border border-white/10 p-6 md:p-8 hover:-translate-y-1 transition-transform duration-300 group"
      data-testid={`story-card-${story.id}`}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-[#FF3B30]/20 border border-[#FF3B30]/40 flex items-center justify-center shrink-0">
          <span className="font-['Anton'] text-[#FF3B30] text-lg">{story.author_name.charAt(0).toUpperCase()}</span>
        </div>
        <div>
          <p className="text-sm font-semibold text-white">{story.author_name}</p>
          <div className="flex items-center gap-3 text-xs text-white/30">
            {story.location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {story.location}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {new Date(story.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>
      <h3 className="font-['Anton'] text-lg uppercase tracking-tight text-white mb-3 group-hover:text-[#FF3B30] transition-colors">
        {story.title}
      </h3>
      <p className="text-sm leading-relaxed text-white/60 mb-5">{story.story}</p>

      {/* Share buttons */}
      <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-white/5">
        <span className="text-xs uppercase tracking-[0.15em] text-white/25 font-bold mr-1 flex items-center gap-1">
          <Share2 className="w-3 h-3" /> Share
        </span>
        <ShareButton
          href={twitterUrl}
          label="X"
          icon={<XIcon />}
          className="border-white/10 text-white/40 hover:border-white/30 hover:text-white hover:bg-white/5"
          testId={`share-x-${story.id}`}
        />
        <ShareButton
          href={facebookUrl}
          label="Facebook"
          icon={<FacebookIcon />}
          className="border-white/10 text-white/40 hover:border-[#1877F2]/50 hover:text-[#1877F2] hover:bg-[#1877F2]/5"
          testId={`share-facebook-${story.id}`}
        />
        <ShareButton
          href={linkedinUrl}
          label="LinkedIn"
          icon={<LinkedInIcon />}
          className="border-white/10 text-white/40 hover:border-[#0A66C2]/50 hover:text-[#0A66C2] hover:bg-[#0A66C2]/5"
          testId={`share-linkedin-${story.id}`}
        />
        <ShareButton
          href={redditUrl}
          label="Reddit"
          icon={<RedditIcon />}
          className="border-white/10 text-white/40 hover:border-[#FF4500]/50 hover:text-[#FF4500] hover:bg-[#FF4500]/5"
          testId={`share-reddit-${story.id}`}
        />
        <ShareButton
          onClick={handleCopyLink}
          label={copied ? "Copied!" : "Copy Link"}
          icon={copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          className={`border-white/10 ${copied ? "text-green-400 border-green-400/30" : "text-white/40 hover:border-[#FFCC00]/50 hover:text-[#FFCC00] hover:bg-[#FFCC00]/5"}`}
          testId={`share-copy-${story.id}`}
        />
      </div>
    </div>
  );
};

const SubmitStoryForm = ({ onSubmitted }) => {
  const [form, setForm] = useState({ author_name: "", location: "", title: "", story: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.author_name || !form.title || !form.story) return;
    setLoading(true);
    try {
      await axios.post(`${API}/stories`, form);
      toast.success("Your story has been published! Thank you for sharing.");
      setForm({ author_name: "", location: "", title: "", story: "" });
      onSubmitted();
    } catch {
      toast.error("Failed to submit story. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="border border-white/10 p-8 md:p-10 space-y-6" data-testid="submit-story-form">
      <div className="flex items-center gap-3 mb-2">
        <PenLine className="w-5 h-5 text-[#FF3B30]" />
        <h3 className="font-['Anton'] text-xl uppercase tracking-tight text-white">
          SHARE YOUR <span className="text-[#FFCC00]">STORY</span>
        </h3>
      </div>
      <p className="text-sm text-white/50">
        Your experience matters. Share your EMF encounter to help raise awareness and connect with others.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/50 mb-2 block">Your Name *</label>
          <Input
            name="author_name"
            value={form.author_name}
            onChange={handleChange}
            placeholder="Your name or alias"
            className="rounded-none bg-zinc-900/50 border border-white/15 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] px-4 py-3 h-12 text-white placeholder:text-white/40"
            required
            data-testid="story-author-input"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/50 mb-2 block">Location (Optional)</label>
          <Input
            name="location"
            value={form.location}
            onChange={handleChange}
            placeholder="City, Country"
            className="rounded-none bg-zinc-900/50 border border-white/15 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] px-4 py-3 h-12 text-white placeholder:text-white/40"
            data-testid="story-location-input"
          />
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/50 mb-2 block">Title *</label>
        <Input
          name="title"
          value={form.title}
          onChange={handleChange}
          placeholder="Give your story a title"
          className="rounded-none bg-zinc-900/50 border border-white/15 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] px-4 py-3 h-12 text-white placeholder:text-white/40"
          required
          data-testid="story-title-input"
        />
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/50 mb-2 block">Your Story *</label>
        <Textarea
          name="story"
          value={form.story}
          onChange={handleChange}
          placeholder="Describe your experience with EMF exposure — what happened, how it affected you, and what you learned..."
          rows={8}
          className="rounded-none bg-zinc-900/50 border border-white/15 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] px-4 py-3 text-white placeholder:text-white/40 resize-none"
          required
          data-testid="story-text-textarea"
        />
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full rounded-none uppercase tracking-widest font-bold bg-[#FF3B30] text-white border-2 border-[#FF3B30] hover:bg-transparent hover:text-[#FF3B30] transition-all duration-300 h-12"
        data-testid="story-submit-btn"
      >
        <Send className="w-4 h-4 mr-2" />
        {loading ? "PUBLISHING..." : "PUBLISH YOUR STORY"}
      </Button>
    </form>
  );
};

export default function CommunityPage() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/stories`);
      setStories(data);
    } catch (err) {
      console.error("Failed to fetch stories:", err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStories();
  }, [fetchStories]);

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="community-page">
      <Header />

      {/* Hero */}
      <section className="pt-28 pb-16 md:pt-36 md:pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF3B30] mb-4">
            Collective Testimony
          </p>
          <h1
            className="font-['Anton'] text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter leading-[0.9] text-white mb-6"
            data-testid="community-title"
          >
            COMMUNITY <span className="text-[#FFCC00]">STORIES</span>
          </h1>
          <p className="text-base sm:text-lg leading-relaxed text-white/60 max-w-2xl">
            Real experiences from real people. Every story shared strengthens our
            collective understanding of the EMF threat and helps others who may
            be experiencing similar effects.
          </p>
        </div>
      </section>

      <Separator className="bg-white/10" />

      {/* Stories + Form */}
      <section className="py-16 md:py-24 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Stories list */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-8">
              <Users className="w-5 h-5 text-[#FF3B30]" />
              <h2 className="font-['Anton'] text-xl uppercase tracking-tight text-white">
                {stories.length} {stories.length === 1 ? "STORY" : "STORIES"} SHARED
              </h2>
            </div>

            {loading && (
              <div className="text-white/30 py-12 text-center">Loading stories...</div>
            )}
            {!loading && stories.length === 0 && (
              <div className="border border-white/10 p-12 text-center" data-testid="no-stories-message">
                <BookOpen className="w-10 h-10 text-white/20 mx-auto mb-4" />
                <p className="text-white/40 mb-2">No stories shared yet.</p>
                <p className="text-sm text-white/30">Be the first to share your experience.</p>
              </div>
            )}
            {!loading && stories.length > 0 && (
              <div className="space-y-0">
                {stories.map((story) => (
                  <StoryCard key={story.id} story={story} />
                ))}
              </div>
            )}
          </div>

          {/* Submit form */}
          <div className="lg:col-span-1">
            <SubmitStoryForm onSubmitted={fetchStories} />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#FF3B30]" />
            <span className="font-['Anton'] text-sm uppercase tracking-wider text-white">
              EMF<span className="text-[#FF3B30]">AWARE</span>
            </span>
          </div>
          <Link to="/" className="text-xs text-white/30 hover:text-[#FF3B30] transition-colors flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Back to main site
          </Link>
        </div>
      </footer>

      <Toaster
        position="bottom-right"
        toastOptions={TOAST_OPTIONS}
      />
    </div>
  );
}
